<template>
  <div id="price-communication-modal">
    <div class="header-container">
      <modal-header
        class="modal-header"
        :hasSeparator="false"
        :titleText="titleText"
        />
    </div>
    <div v-if="!isLoading" class="modal-content">
      <div class="section-one">
        {{ sectionOne }}
      </div>
      <div class="section-two">
        {{ sectionTwo }}
      </div>
      <div class="section-three">
        {{ sectionThree }}
      </div>
      <div class="section-four">
        <inline-link class="link" :text="$t('app.priceCommunication.modal.moreInformation')" @inlineLinkClicked="linkAction"/>
      </div>
    </div>
    <div v-else class="modal-content-loading">
      <div class="section-two-shimming">
        <shimming-line
          class="loading-line"
          :width="368"
          :height="20"
          :radiusLeftRight="4"
          :radiusTopBottom="4"
        />
        <shimming-line
          class="loading-line"
          :width="360"
          :height="20"
          :radiusLeftRight="4"
          :radiusTopBottom="4"
        />
        <shimming-line
          class="loading-line"
          :width="340"
          :height="20"
          :radiusLeftRight="4"
          :radiusTopBottom="4"
        />
      </div>
      <div class="section-one-shimming">
        <shimming-line
          class="loading-line"
          :width="380"
          :height="20"
          :radiusLeftRight="4"
          :radiusTopBottom="4"
        />
        <shimming-line
          class="loading-line"
          :width="300"
          :height="20"
          :radiusLeftRight="4"
          :radiusTopBottom="4"
        />
      </div>
      <div class="section-three-shimming">
        <shimming-line
          class="loading-line"
          :width="340"
          :height="20"
          :radiusLeftRight="4"
          :radiusTopBottom="4"
        />
        <shimming-line
          class="loading-line"
          :width="320"
          :height="20"
          :radiusLeftRight="4"
          :radiusTopBottom="4"
        />
      </div>
    </div>
    <div class="button-container">
      <rounded-button class="ok-button" :text="$t('app.general.texts.general.confirm')" font-size="16" @roundedButtonClicked="okAction"/>
    </div>
  </div>
</template>

<script>
import ShimmingLine from '@/components/shared/elements/shimming/ShimmingLine';
import RoundedButton from '@/components/shared/elements/buttons/RoundedButton';
import { mapActions, mapGetters } from 'vuex';
import ModalHeader from './ModalHeader';


export default {
  name: 'PriceCommunicationModal',
  components: {
    ModalHeader,
    ShimmingLine,
    RoundedButton,
  },
  data() {
    return {
      bestProduct: undefined,
      exchangeValue: undefined,
      isLoading: false,
    };
  },
  props: {
    product: {
      type: Object,
      required: true,
    },
    discountText: {
      type: String,
      required: true,
    },
    monthlyProduct: {
      type: Object,
      required: true,
    },
    currency: {
      type: String,
      required: true,
    },
    lastModalProps: {
      type: Object,
      required: false,
      default: undefined,
    },
  },
  async created() {
    this.isLoading = true;
    await this.getExchangeRate(this.currency).then((response) => {
      this.exchangeValue = response.response.rate;
    });
    this.getBestPriceId(this.product.braintreeId)
      .then((response) => {
        this.getProduct(response.response.product_code)
          .then((productResponse) => {
            this.bestProduct = productResponse;
            this.isLoading = false;
          });
      });
  },
  methods: {
    ...mapActions({
      getBestPriceId: 'subscriptions/getBestPriceId',
      getProduct: 'subscriptions/getProduct',
      getExchangeRate: 'subscriptions/getExchangeRate',
    }),
    convertPrice(price) {
      return (price * this.exchangeValue)
        .toFixed(0)
        .toString()
        .replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    },
    okAction() {
      this.$eventBus.closeModal();
    },
    linkAction() {
      if (this.getLang === 'hu_HU') {
        window.open('https://segitseg.xeropan.com/help/mennyibe-kerulnek-a-pro-elofizetesek-es-milyen-konstrukciokban-erhetoek-el');
      } else {
        window.open('https://helpcenter.xeropan.com/help/how-much-do-pro-subscriptions-cost-and-what-plans-are-available');
      }
    },
  },
  computed: {
    ...mapGetters({
      getLang: 'settings/userSelectedLanguage',
    }),
    sectionOneCalculation() {
      return `${this.product.renewMonthPeriod} * ${this.convertPrice(this.monthlyProduct.price)} ${this.currency} = ${this.convertPrice(this.monthlyProduct.price * this.product.renewMonthPeriod)} ${this.currency}`
    },
    sectionTwoCalculation() {
      return `${this.convertPrice(this.bestProduct.price)} ${this.currency}`;
    },
    sectionThreeCalculation() {
      return `${this.calculatedDiscount}`;
    },
    titleText() {
      if (document.documentElement.lang == 'ar') {
        return `${this.$t('app.priceCommunication.modal.title.month')} ${this.product.renewMonthPeriod} / ${this.currency} ${this.convertPrice(this.product.price)}`;
      }
      return `${this.convertPrice(this.product.price)} ${this.currency} / ${this.product.renewMonthPeriod} ${this.$t('app.priceCommunication.modal.title.month')}`;
    },
    sectionOne() {
      return `${this.$t('app.priceCommunication.modal.sectionOne', { discountText: this.discountText, sectionOneCalculation: this.sectionOneCalculation })}`;
    },
    sectionTwo() {
      return `${this.$t('app.priceCommunication.modal.sectionTwo', { sectionTwoCalculation: this.sectionTwoCalculation })}`;
    },
    sectionThree() {
      return `${this.$t('app.priceCommunication.modal.sectionThree', { sectionThreeCalculation: this.sectionThreeCalculation })}`;
    },
    calculatedDiscount() {
      if (this.product.price < this.bestProduct.price) {
        return `${(((this.bestProduct.price / this.product.price) * 100)).toFixed(0)}`;
      }
      if (((this.bestProduct.price / this.product.price) * 100) === 100) {
        return '0';
      }
      return `${(((this.bestProduct.price / this.product.price) * 100) - 100).toFixed(0)}`;
    },
  },
};
</script>

<style lang="scss" scoped>
#price-communication-modal {
  width: 440px;
  padding: 25px 0px 24px 0px;
  .modal-content {
    padding: 23px 24px;
    font-size: 15.5px;
    line-height: 22px;
    font-family: Roboto;
    color: var(--terraGrey);
    .section-one {
      padding-right: 22px;
    }
    .section-two {
      margin-top: 22px;
    }
    .section-four {
      margin-top: 22px;
      /deep/ .inline-link {
        padding-left: 0px;
        padding-right: 22px;
        .link {
          color: var(--primaryBlue) !important;
        }
      }
    }
  }
  .modal-content-loading {
    padding: 23px 24px;
    line-height: 22px;
    .section-one-shimming {
      margin-top: 22px;
    }
    .section-two-shimming {
    }
    .section-three-shimming {
      margin-top: 22px;
    }
  }
  .button-container {
    .ok-button {
      height: 44px;
    }
  }
}
</style>
